
import React, { useState } from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Navigation } from '@/components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Building2, ArrowLeft, ChevronRight, Edit, Trash2, Plus, CreditCard } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { OrganizationForm } from '@/components/shared/OrganizationForm';
import { ClubForm } from '@/components/shared/ClubForm';
import { PaymentManagementDialog } from '@/components/payment/PaymentManagementDialog';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

type SportType = 'Baseball' | 'Basketball' | 'Cross Country' | 'Football' | 'Golf' | 'Gymnastics' | 'Ice Hockey' | 'Lacrosse' | 'Soccer' | 'Softball' | 'Swimming' | 'Tennis' | 'Track and Field' | 'Volleyball' | 'Water Polo' | 'Wrestling' | 'Badminton' | 'Bowling' | 'Cheerleading' | 'Dance' | 'Field Hockey' | 'Rowing' | 'Rugby' | 'Skiing' | 'Table Tennis';

const SuperAdminOrganization = () => {
  const { orgId } = useParams();
  const { profile, loading, isImpersonating, effectiveRole } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [hoveredClubId, setHoveredClubId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo_url: '',
    website: '',
  });

  // Club form state
  const [isClubDialogOpen, setIsClubDialogOpen] = useState(false);
  const [clubName, setClubName] = useState('');
  const [clubLocation, setClubLocation] = useState('');
  const [clubWebsite, setClubWebsite] = useState('');
  const [clubProfilePicture, setClubProfilePicture] = useState('');
  const [clubSport, setClubSport] = useState<SportType | ''>('');

  const { data: organization, isLoading: orgLoading } = useQuery({
    queryKey: ['organization', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', orgId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!orgId,
  });

  const { data: clubs, isLoading: clubsLoading } = useQuery({
    queryKey: ['organization-clubs', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clubs')
        .select('*')
        .eq('organization_id', orgId)
        .order('name');
      
      if (error) throw error;
      return data;
    },
    enabled: !!orgId,
  });

  const updateOrgMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('organizations')
        .update({
          name: formData.name,
          description: formData.description || null,
          logo_url: formData.logo_url || null,
          website: formData.website || null,
        })
        .eq('id', orgId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization', orgId] });
      toast.success('Organization updated successfully');
      setIsEditDialogOpen(false);
    },
    onError: (error) => {
      console.error('Error updating organization:', error);
      toast.error('Failed to update organization');
    },
  });

  const deleteOrgMutation = useMutation({
    mutationFn: async () => {
      // Check if organization has clubs
      const { data: clubs, error: clubsError } = await supabase
        .from('clubs')
        .select('id, name')
        .eq('organization_id', orgId);
      
      if (clubsError) throw clubsError;
      
      if (clubs && clubs.length > 0) {
        const clubNames = clubs.map(c => c.name).join(', ');
        throw new Error(`Cannot delete organization with existing clubs. Please delete the following clubs first: ${clubNames}`);
      }

      const { error } = await supabase
        .from('organizations')
        .delete()
        .eq('id', orgId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Organization deleted successfully');
      navigate('/superadmin');
    },
    onError: (error) => {
      console.error('Error deleting organization:', error);
      if (error.message.includes('Cannot delete organization with existing clubs')) {
        toast.error(error.message);
      } else {
        toast.error('Failed to delete organization');
      }
    },
  });

  const createClubMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('clubs')
        .insert({
          name: clubName,
          location: clubLocation || null,
          website: clubWebsite || null,
          profile_picture_url: clubProfilePicture || null,
          sport: (clubSport as SportType) || null,
          organization_id: orgId
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-clubs', orgId] });
      toast.success('Club created successfully');
      setIsClubDialogOpen(false);
      resetClubForm();
    },
    onError: (error) => {
      console.error('Error creating club:', error);
      toast.error('Failed to create club');
    },
  });

  const handleEdit = () => {
    if (!organization) return;
    setFormData({
      name: organization.name,
      description: organization.description || '',
      logo_url: organization.logo_url || '',
      website: organization.website || '',
    });
    setIsEditDialogOpen(true);
  };

  const handleDelete = () => {
    setDeleteConfirmText('');
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmText === organization?.name) {
      deleteOrgMutation.mutate();
      setIsDeleteDialogOpen(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    updateOrgMutation.mutate();
  };

  const handleClubSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!clubName.trim()) return;
    createClubMutation.mutate();
  };

  const resetClubForm = () => {
    setClubName('');
    setClubLocation('');
    setClubWebsite('');
    setClubProfilePicture('');
    setClubSport('');
  };

  // Redirect if not superadmin or if currently impersonating
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }


  if (!profile || effectiveRole !== 'superadmin' || isImpersonating) {
    return <Navigate to="/" replace />;
  }

  if (orgLoading || clubsLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6 text-sm text-muted-foreground">
          <Link to="/superadmin" className="hover:text-foreground">
            Super Admin
          </Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-foreground">{organization?.name}</span>
        </div>

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={organization?.logo_url} alt={organization?.name} />
                <AvatarFallback>
                  <Building2 className="h-6 w-6 text-blue-600" />
                </AvatarFallback>
              </Avatar>
              {organization?.name}
            </h1>
            <p className="text-muted-foreground mt-2">
              Manage clubs and members in this organization
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handleEdit} className="w-full sm:w-auto">
              <Edit className="h-4 w-4 mr-2" />
              Edit Org
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="w-full sm:w-auto">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Org
            </Button>
            <Button asChild variant="outline" className="w-full sm:w-auto">
              <Link to="/superadmin">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Super Admin
              </Link>
            </Button>
          </div>
        </div>

        {/* Organization Details */}
        <div className="grid gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle>Organization Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {organization?.description && (
                <p className="text-muted-foreground">{organization.description}</p>
              )}
              {organization?.website && (
                <a 
                  href={organization.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline inline-block"
                >
                  Visit Website
                </a>
              )}
              <div className="pt-4 border-t">
                <PaymentManagementDialog
                  entityType="organization"
                  entityId={orgId!}
                  entityName={organization?.name || ''}
                >
                  <Button variant="outline" className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Manage Payments
                  </Button>
                </PaymentManagementDialog>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Clubs Grid */}
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Clubs ({clubs?.length || 0})</h2>
            <Button onClick={() => setIsClubDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Club
            </Button>
          </div>

          {clubs && clubs.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {clubs.map((club) => (
                <Card 
                  key={club.id} 
                  className="hover:shadow-lg transition-shadow relative overflow-hidden"
                  onMouseEnter={() => setHoveredClubId(club.id)}
                  onMouseLeave={() => setHoveredClubId(null)}
                >
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={club.profile_picture_url} alt={club.name} />
                        <AvatarFallback>
                          <Building2 className="h-4 w-4" />
                        </AvatarFallback>
                      </Avatar>
                      {club.name}
                    </CardTitle>
                    {club.location && (
                      <CardDescription>{club.location}</CardDescription>
                    )}
                    {club.sport && (
                      <div className="mt-2">
                        <Badge variant="secondary" className="text-xs">
                          {club.sport}
                        </Badge>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="flex justify-end items-center">
                      {/* Always render but control visibility */}
                      <div className={`transition-opacity duration-200 ${
                        hoveredClubId === club.id ? 'opacity-100 visible' : 'opacity-0 invisible'
                      }`}>
                        <Button asChild size="sm">
                          <Link to={`/superadmin/club/${club.id}`}>
                            Manage Club
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-8 text-center">
                <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Clubs Found</h3>
                <p className="text-muted-foreground">
                  This organization doesn't have any clubs yet.
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Edit Organization Dialog */}
        <OrganizationForm
          isOpen={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          editingOrg={organization}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          isLoading={updateOrgMutation.isPending}
        />

        {/* Add Club Dialog */}
        <ClubForm
          isOpen={isClubDialogOpen}
          onOpenChange={setIsClubDialogOpen}
          onSubmit={handleClubSubmit}
          clubName={clubName}
          setClubName={setClubName}
          clubLocation={clubLocation}
          setClubLocation={setClubLocation}
          clubWebsite={clubWebsite}
          setClubWebsite={setClubWebsite}
          clubProfilePicture={clubProfilePicture}
          setClubProfilePicture={setClubProfilePicture}
          clubSport={clubSport}
          setClubSport={setClubSport}
          isSubmitting={createClubMutation.isPending}
          isEditing={false}
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Organization</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the organization "{organization?.name}" 
                and all associated data including clubs, teams, and members.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <Label htmlFor="confirm-name">
                Type the organization name "{organization?.name}" to confirm deletion:
              </Label>
              <Input
                id="confirm-name"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={organization?.name}
                className="mt-2"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                disabled={deleteConfirmText !== organization?.name || deleteOrgMutation.isPending}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleteOrgMutation.isPending ? 'Deleting...' : 'Delete Organization'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default SuperAdminOrganization;
